/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  AfterViewInit,
  Component,
  computed,
  effect,
  ElementRef,
  inject,
  input,
  OnChanges,
  OnDestroy,
  signal,
  SimpleChanges,
  viewChild,
} from '@angular/core';
import * as Plotly from 'plotly.js-dist-min';
import { Config, Data, Layout } from 'plotly.js-dist-min';
import { fromEvent, debounceTime, Subject, takeUntil } from 'rxjs';
import {
  ExperimentData,
  IDevice,
  IFlask,
  ITimeValue,
} from 'src/app/_models/experiment-data';
import {
  MatSlideToggle,
  MatSlideToggleChange,
} from '@angular/material/slide-toggle';
import { FormBuilder, Validators } from '@angular/forms';
import { FormBase } from 'src/app/_models/form-base';
import { UnderlinedDropdownFormFieldComponent } from '../../forms/underlined-dropdown-form-field/underlined-dropdown-form-field.component';
import { ButtonComponent } from '../../buttons/button/button.component';
import { UnderlinedFormFieldComponent } from '../../forms/underlined-form-field/underlined-form-field.component';
import { Device } from 'src/app/_models/device';
import { MatIconButtonComponent } from '../../buttons/mat-icon-button/mat-icon-button.component';
import { AdvancedSettings } from 'src/app/_models/advanced-settings';
import { SlideToggleGroupComponent } from '../../selection-controls/slide-toggle-group/slide-toggle-group.component';
import { FluorescenceChannel } from 'src/app/_models/fluorescence-channel';

@Component({
  selector: 'app-fluoro-experiment-graph',
  templateUrl: './fluoro-experiment-graph.component.html',
  styleUrls: ['./fluoro-experiment-graph.component.scss'],
  imports: [
    MatSlideToggle,
    UnderlinedDropdownFormFieldComponent,
    ButtonComponent,
    UnderlinedFormFieldComponent,
    MatIconButtonComponent,
    SlideToggleGroupComponent,
  ],
})
export class FluoroExperimentGraphComponent
  implements AfterViewInit, OnChanges, OnDestroy
{
  readonly plotlyDiv = viewChild<ElementRef>('plotlyDiv');

  private readonly destroy$ = new Subject<void>();
  protected formBuilder = inject(FormBuilder);

  // Colour scale - same as D3 version
  private readonly coloursList: string[][] = [
    ['#6ECF9C', '#6C73C1', '#CE517E', '#37494A'],
    ['#F03E2B', '#BE4FC1', '#CB9F50', '#7FCE4C'],
    ['#5BB4E9', '#FFA62A', '#969696', '#D6A7D7'],
    ['#346684', '#496A35', '#553056', '#AA6E5C'],
  ];

  // Plotly symbols for different channels
  private readonly symbolTypes = ['circle', 'square', 'triangle-up', 'diamond'];

  checked = signal(false);
  dragMode = signal<'zoom' | 'pan'>('zoom');
  graphMode = signal<'lines' | 'markers' | 'lines+markers'>('markers');
  displayChannelFilter = signal(false);
  private graphReady = false;
  errorMessage: string | null = null;
  private readonly margin = {
    left: 70,
    right: 5,
    top: 10,
    bottom: 60,
  };

  private width = 800;
  private height = 450;
  private readonly maxHeight = 450;

  // Point magnification settings
  private readonly minMarkerSize = 2;
  private readonly maxMarkerSize = 12;
  private baseMarkerSize = 8;
  private initialXRange: [number, number] | null = null;
  private initialYRange: [number, number] | null = null;

  dataToRender: IRenderData[] = [];
  filteredDataToRender: IRenderData[] = [];
  private labelList: IPropertyColour[] = [];
  legendData = signal<ILegendDevice[]>([]);

  // Channel filter state
  selectedChannels = signal<number[]>([]);

  // Form variables
  minGraphHeight = 200;
  minGraphWidth = 200;
  advancedSettings: AdvancedSettings;

  // Signal inputs
  readonly graphId = input('fluoro-scatter');
  readonly yAxisLabel = input('Fluorescence [AU]');
  readonly experimentData = input<ExperimentData>();
  readonly fluorescenceChannels = input<FluorescenceChannel[]>([]);
  readonly devices = input<Device[]>();
  readonly experimentName = input<string>();

  private previousExperimentData: ExperimentData | undefined;

  widthField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-width`,
      label: 'Width (px)',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  heightField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-height`,
      label: 'Height (px)',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  yMinRangeField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-yMinRange`,
      label: 'y Min',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  yMaxRangeField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-yMaxRange`,
      label: 'y Max',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  xMinRangeField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-xMinRange`,
      label: 'x Min',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  xMaxRangeField = computed(() =>
    new FormBase({
      key: `${this.graphId()}-xMaxRange`,
      label: 'x Max',
      type: 'number',
      placeholder: '',
      disabled: false,
      required: false,
      value: '',
      options: [],
    }),
  );

  ratioField = computed(
    () =>
      new FormBase({
        key: `${this.graphId()}-ratio`,
        label: 'Aspect Ratio',
        type: 'dropdown',
        placeholder: '',
        disabled: false,
        required: false,
        value: 'default',
        options: [
          { key: 'default', value: '1.8:1' },
          { key: 'square', value: '1:1' },
          { key: 'threeByTwo', value: '3:2' },
        ],
      }),
  );

  channelFilterField = computed(() => {
    const channels = this.fluorescenceChannels();
    const options = channels?.map((channel) => ({
      key: channel.memorySlot,
      value: channel.channelName,
    })) || [];

    return new FormBase({
      key: `${this.graphId()}-channelFilter`,
      label: 'Fluorescence Channel',
      type: 'toggle',
      placeholder: 'Select data',
      disabled: channels?.length === 1,
      required: false,
      value: null,
      options,
    });
  });

  advancedSettingsForm = computed(() =>
    this.formBuilder.group({
      [this.widthField().key]: [
        this.widthField().value,
        Validators.compose([
          Validators.required,
          Validators.min(this.minGraphWidth),
        ]),
      ],
      [this.heightField().key]: [
        this.heightField().value,
        Validators.compose([
          Validators.required,
          Validators.min(this.minGraphHeight),
        ]),
      ],
      [this.yMinRangeField().key]: [this.yMinRangeField().value, Validators.required],
      [this.yMaxRangeField().key]: [
        this.yMaxRangeField().value,
        [Validators.required],
      ],
      [this.xMinRangeField().key]: [
        this.xMinRangeField().value,
        [Validators.required],
      ],
      [this.xMaxRangeField().key]: [
        this.xMaxRangeField().value,
        [Validators.required],
      ],
      [this.ratioField().key]: [this.ratioField().value],
    }),
  );

  fluorescenceChannelForm = computed(() =>
    this.formBuilder.group({
      [this.channelFilterField().key]: [this.channelFilterField().value],
    }),
  );

  constructor() {
    // Effect to react to experimentData changes
    effect(() => {
      const data = this.experimentData();
      if (
        data &&
        this.previousExperimentData &&
        data !== this.previousExperimentData
      ) {
        this.dataToRender = [];
        this.labelList = [];
        this.parseData(data, true);
      }
      this.previousExperimentData = data;
    });
  }

  ngAfterViewInit(): void {
    this.initialiseChannelFilter();
    this.initialiseGraph();
  }

  private readonly initialiseChannelFilter = (): void => {
    const channels = this.fluorescenceChannels();
    if (channels && channels.length > 0) {
      this.displayChannelFilter.set(channels.length > 0);
      const defaultChannels = channels.map((channel) => channel.memorySlot);
      this.selectedChannels.set(defaultChannels);
      this.fluorescenceChannelForm().patchValue({
        [this.channelFilterField().key]: defaultChannels,
      });
    }
  };

  private readonly initialiseGraph = (): void => {
    const data = this.experimentData();
    if (data) {
      this.parseData(data);
    }

    // Resize observer
    const resizeObservable$ = fromEvent(window, 'resize').pipe(
      debounceTime(300),
    );

    resizeObservable$.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.handleResize();
    });

    // Channel filter changes
    this.fluorescenceChannelForm()
      .get(this.channelFilterField().key)
      ?.valueChanges.subscribe((values: number[]) => {
        this.selectedChannels.set(values || []);
        this.applyFiltersAndUpdate();
      });

    // Form value changes
    this.advancedSettingsForm()
      .get(this.ratioField().key)
      ?.valueChanges.subscribe((value) => {
        this.updateDimensions(value);
      });

    this.advancedSettingsForm()
      .get(this.xMinRangeField().key)
      ?.valueChanges.subscribe((value) => {
        this.advancedSettingsForm().controls[
          this.xMaxRangeField().key
        ].setValidators([Validators.required, Validators.min(value)]);
        this.advancedSettingsForm().controls[
          this.xMaxRangeField().key
        ].updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        });
      });

    this.advancedSettingsForm()
      .get(this.yMinRangeField().key)
      ?.valueChanges.subscribe((value) => {
        this.advancedSettingsForm().controls[
          this.yMaxRangeField().key
        ].setValidators([Validators.required, Validators.min(value)]);
        this.advancedSettingsForm().controls[
          this.yMaxRangeField().key
        ].updateValueAndValidity({
          onlySelf: true,
          emitEvent: false,
        });
      });
  };

  ngOnChanges(simpleChanges: SimpleChanges): void {
    if (
      simpleChanges['experimentData'] &&
      !simpleChanges['experimentData'].firstChange
    ) {
      this.dataToRender = [];
      this.labelList = [];
      this.parseData(this.experimentData(), true);
    }
  }

  /**
   * Apply current filters and update the graph
   */
  private applyFiltersAndUpdate(): void {
    this.filteredDataToRender = this.filterData();
    this.updateGraph();
  }

  /**
   * Filter data based on selected channels
   */
  filterData(): IRenderData[] {
    const selectedChannelValues = this.selectedChannels();
    if (!selectedChannelValues || selectedChannelValues.length === 0) {
      return [];
    }
    return this.dataToRender.filter((d) =>
      selectedChannelValues.includes(d.MemorySlot),
    );
  }

  /**
   * Safely execute Plotly operations with error handling
   */
  private safePlotlyOperation(operation: () => void, errorMsg: string): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) {
      return;
    }
    try {
      operation();
    } catch (error) {
      this.errorMessage = errorMsg;
    }
  }

  /**
   * Calculate zoom scale and update marker sizes
   */
  private updateMarkerSizeOnZoom(): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement || !this.initialXRange || !this.initialYRange) {
      return;
    }

    const layout = this.plotlyDiv().nativeElement.layout;
    const currentXRange = layout.xaxis?.range;
    const currentYRange = layout.yaxis?.range;

    if (!currentXRange || !currentYRange) return;

    const initialXSpan = this.initialXRange[1] - this.initialXRange[0];
    const initialYSpan = this.initialYRange[1] - this.initialYRange[0];
    const currentXSpan = currentXRange[1] - currentXRange[0];
    const currentYSpan = currentYRange[1] - currentYRange[0];

    const xZoomScale = initialXSpan / currentXSpan;
    const yZoomScale = initialYSpan / currentYSpan;
    const zoomScale = (xZoomScale + yZoomScale) / 2;

    const scaledSize = this.baseMarkerSize * zoomScale;
    const newSize = Math.min(
      Math.max(this.minMarkerSize, scaledSize),
      this.maxMarkerSize
    );

    const traceCount = this.plotlyDiv().nativeElement.data?.length || 0;
    if (traceCount > 0) {
      try {
        // Group traces by symbol type for efficient restyling
        const traces = this.plotlyDiv().nativeElement.data;
        const triangleIndices: number[] = [];
        const otherIndices: number[] = [];

        for (let i = 0; i < traceCount; i++) {
          if (traces[i].marker?.symbol === 'triangle-up') {
            triangleIndices.push(i);
          } else {
            otherIndices.push(i);
          }
        }

        // Apply scaled size to triangles
        if (triangleIndices.length > 0) {
          Plotly.restyle(this.plotlyDiv().nativeElement, { 'marker.size': newSize * 1.3 }, triangleIndices);
        }
        // Apply base size to other shapes
        if (otherIndices.length > 0) {
          Plotly.restyle(this.plotlyDiv().nativeElement, { 'marker.size': newSize }, otherIndices);
        }
      } catch (error) {
        // Silently fail - marker size update is not critical
      }
    }
  }

  /**
   * Clear any active tooltips/hover state
   */
  private clearTooltips(): void {
    if (this.plotlyDiv()?.nativeElement) {
      try {
        Plotly.Fx.hover(this.plotlyDiv().nativeElement, []);
      } catch (error) {
        // Silently fail
      }
    }
  }

  toggleChange(event: MatSlideToggleChange): void {
    this.checked.set(event.checked);

    if (!this.graphReady) {
      return;
    }

    const currentLayout = this.plotlyDiv()?.nativeElement?.layout;
    const currentXRange = currentLayout.xaxis?.range;
    const currentYRange = currentLayout.yaxis?.range;

    const update: Partial<Layout> = {
      xaxis: {
        ...this.getXAxisConfig(),
        range: currentXRange,
        fixedrange: this.checked(),
      },
      yaxis: {
        ...this.getYAxisConfig(),
        range: currentYRange,
        fixedrange: this.checked(),
      },
    };

    this.safePlotlyOperation(
      () => Plotly.relayout(this.plotlyDiv().nativeElement, update),
      'Failed to toggle zoom. Try again'
    );
  }

  resetZoom(): void {
    const { minY, maxY, minX, maxX } = this.getDataBounds(this.filteredDataToRender);

    const xVal = (maxX - minX) * 0.05;
    const yVal = (maxY - minY) * 0.05;

    const totalPoints = this.filteredDataToRender.length;
    const useWebGL = totalPoints > 5000;

    if (this.advancedSettings) {
      this.advancedSettingsForm().patchValue({
        [this.widthField().key]: this.advancedSettings.width.toString(),
        [this.heightField().key]: this.advancedSettings.height.toString(),
        [this.yMinRangeField().key]: this.advancedSettings.yMin.toString(),
        [this.yMaxRangeField().key]: this.advancedSettings.yMax.toString(),
        [this.xMinRangeField().key]: this.advancedSettings.xMin.toString(),
        [this.xMaxRangeField().key]: this.advancedSettings.xMax.toString(),
        [this.ratioField().key]: this.advancedSettings.aspectRatio,
      });
    }

    const update: Partial<Layout> = {
      'xaxis.range': [minX - xVal, maxX + xVal],
      'yaxis.range': [minY - yVal, maxY + yVal],
    };

    if (useWebGL) {
      this.safePlotlyOperation(
        () => {
          Plotly.relayout(this.plotlyDiv().nativeElement, update);
          this.clearTooltips();
        },
        'Failed to reset zoom. Please try again.'
      );
    } else {
      this.safePlotlyOperation(
        () => {
          Plotly.animate(
            this.plotlyDiv().nativeElement,
            { layout: update },
            {
              transition: {
                duration: 750,
                easing: 'cubic-in-out',
              },
              frame: {
                duration: 750,
              },
            },
          );
          this.clearTooltips();
        },
        'Failed to reset zoom. Please try again.'
      );
    }
  }

  zoomIn(): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) return;

    const layout = this.plotlyDiv().nativeElement.layout;
    const xRange = layout.xaxis.range;
    const yRange = layout.yaxis.range;

    const xCenter = (xRange[0] + xRange[1]) / 2;
    const yCenter = (yRange[0] + yRange[1]) / 2;
    const xHalf = (xRange[1] - xRange[0]) / 4;
    const yHalf = (yRange[1] - yRange[0]) / 4;

    this.safePlotlyOperation(
      () => {
        Plotly.relayout(this.plotlyDiv().nativeElement, {
          'xaxis.range': [xCenter - xHalf, xCenter + xHalf],
          'yaxis.range': [yCenter - yHalf, yCenter + yHalf],
        });
        this.clearTooltips();
      },
      'Failed to zoom in. Please try again.'
    );
  }

  zoomOut(): void {
    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) return;

    const layout = this.plotlyDiv().nativeElement.layout;
    const xRange = layout.xaxis.range;
    const yRange = layout.yaxis.range;

    const xCenter = (xRange[0] + xRange[1]) / 2;
    const yCenter = (yRange[0] + yRange[1]) / 2;
    const xHalf = xRange[1] - xRange[0];
    const yHalf = yRange[1] - yRange[0];

    this.safePlotlyOperation(
      () => {
        Plotly.relayout(this.plotlyDiv().nativeElement, {
          'xaxis.range': [xCenter - xHalf, xCenter + xHalf],
          'yaxis.range': [yCenter - yHalf, yCenter + yHalf],
        });
        this.clearTooltips();
      },
      'Failed to zoom out. Please try again.'
    );
  }

  setDragMode(mode: 'zoom' | 'pan'): void {
    this.dragMode.set(mode);
    this.safePlotlyOperation(
      () => Plotly.relayout(this.plotlyDiv().nativeElement, { dragmode: mode }),
      'Failed to change drag mode. Please try again.'
    );
  }

  setGraphMode(mode: 'lines' | 'markers' | 'lines+markers'): void {
    this.graphMode.set(mode);

    if (!this.graphReady || !this.plotlyDiv()?.nativeElement) return;

    const traceCount = this.plotlyDiv().nativeElement.data?.length || 0;
    if (traceCount > 0) {
      const traceIndices = Array.from({ length: traceCount }, (_, i) => i);
      this.safePlotlyOperation(
        () => Plotly.restyle(this.plotlyDiv().nativeElement, { mode }, traceIndices),
        'Failed to change graph mode. Please try again.'
      );
    }
  }

  downloadImage(): void {
    this.safePlotlyOperation(
      () => Plotly.downloadImage(this.plotlyDiv().nativeElement, {
        format: 'png',
        filename: `${this.experimentName()} - Fluorescence`,
        width: this.width + this.margin.left + this.margin.right,
        height: this.height + this.margin.top + this.margin.bottom,
      }),
      'Failed to download image. Please try again.'
    );
  }

  resizeGraph(): void {
    const width = Number.parseInt(this.advancedSettingsForm().controls[this.widthField().key]?.value);
    const height = Number.parseInt(
      this.advancedSettingsForm().controls[this.heightField().key]?.value,
    );
    const xMin = Number.parseFloat(
      this.advancedSettingsForm().controls[this.xMinRangeField().key]?.value,
    );
    const xMax = Number.parseFloat(
      this.advancedSettingsForm().controls[this.xMaxRangeField().key]?.value,
    );
    const yMin = Number.parseFloat(
      this.advancedSettingsForm().controls[this.yMinRangeField().key]?.value,
    );
    const yMax = Number.parseFloat(
      this.advancedSettingsForm().controls[this.yMaxRangeField().key]?.value,
    );

    if (Number.isNaN(width) || Number.isNaN(height) || Number.isNaN(xMin) || Number.isNaN(xMax) || Number.isNaN(yMin) || Number.isNaN(yMax)) {
      this.errorMessage = 'Invalid dimensions provided. Please check all input values are valid numbers.';
      return;
    }

    if (width <= 0 || height <= 0) {
      this.errorMessage = 'Width and height must be greater than zero.';
      return;
    }

    if (xMin >= xMax) {
      this.errorMessage = 'X-axis minimum must be less than maximum.';
      return;
    }

    if (yMin >= yMax) {
      this.errorMessage = 'Y-axis minimum must be less than maximum.';
      return;
    }

    const xVal = (xMax - xMin) * 0.05;
    const yVal = (yMax - yMin) * 0.05;

    this.width = width;
    this.height = height;

    this.safePlotlyOperation(
      () => {
        Plotly.relayout(this.plotlyDiv().nativeElement, {
          width: width + this.margin.left + this.margin.right,
          height: height + this.margin.top + this.margin.bottom,
          'xaxis.range': [xMin - xVal, xMax + xVal],
          'yaxis.range': [yMin - yVal, yMax + yVal],
        });
        this.clearTooltips();
      },
      'Failed to resize graph. Please check your dimensions and try again.'
    );
  }

  updateSizeFormValues(): void {
    this.advancedSettingsForm().patchValue({
      [this.widthField().key]: this.width.toFixed(2).toString(),
      [this.heightField().key]: this.height.toFixed(2).toString(),
    });
  }

  private handleResize(): void {
    const value =
      this.advancedSettingsForm().controls[this.ratioField().key]?.value || 'default';
    this.updateDimensions(value);
  }

  private updateDimensions(ratio?: string): void {
    setTimeout(() => {
      if (!this.plotlyDiv()?.nativeElement) return;

      const rect = this.plotlyDiv().nativeElement.getBoundingClientRect();
      const containerWidth = rect.width > 0 ? rect.width : 800;

      const maxWidth = containerWidth - (this.margin.left + this.margin.right);

      if (!ratio || ratio === 'default') {
        this.width = maxWidth;
        const desiredHeight = maxWidth / 1.8;
        this.height = Math.min(desiredHeight, this.maxHeight);
      } else if (ratio === 'square') {
        const size = Math.min(maxWidth, this.maxHeight);
        this.width = size;
        this.height = size;
      } else if (ratio === 'threeByTwo') {
        const desiredHeight = maxWidth / 1.5;
        if (desiredHeight <= this.maxHeight) {
          this.width = maxWidth;
          this.height = desiredHeight;
        } else {
          this.height = this.maxHeight;
          this.width = this.maxHeight * 1.5;
        }
      }

      this.updateSizeFormValues();

      const newLayout = {
        width: this.width + this.margin.left + this.margin.right,
        height: this.height + this.margin.top + this.margin.bottom,
      };

      this.safePlotlyOperation(
        () => {
          Plotly.relayout(this.plotlyDiv().nativeElement, newLayout);
          this.clearTooltips();
        },
        'Failed to update graph dimensions. Please try again.'
      );
    }, 0);
  }

  private readonly parseData = (
    experimentData: ExperimentData,
    isUpdate = false,
  ): void => {
    const property = experimentData.dataType;
    const deviceList: IDevice[] = experimentData.devices;
    const channels = this.fluorescenceChannels();

    deviceList?.forEach((dataSet, index) => {
      const validFlasks = dataSet.flasks.filter(
        (flask) => flask && Object.keys(flask).length > 0 && flask.name && flask.values,
      );

      validFlasks.forEach((flask: IFlask) => {
        const deviceFlaskProperty = `${dataSet.deviceId}_${flask.name}_${property}`;

        if (
          flask.values &&
          Array.isArray(flask.values) &&
          flask.values.length > 0
        ) {
          flask.values.forEach((timeValue: ITimeValue) => {
            if (
              timeValue &&
              typeof timeValue.time === 'number' &&
              typeof timeValue.value === 'number'
            ) {
              const y = timeValue.value;
              const x = timeValue.time;

              const channel = flask.name.slice(0, -2);
              const channelIndex = channels?.findIndex(
                (ch) => ch.channelName.toLowerCase() === channel.toLowerCase(),
              ) ?? -1;

              this.dataToRender.push({
                DeviceFlaskProperty: deviceFlaskProperty,
                Time: x,
                Value: y,
                DeviceName: dataSet.deviceId.toString(),
                FlaskName: flask.name,
                Selection: channelIndex,
                MemorySlot: channels?.[channelIndex]?.memorySlot,
              });
            }
          });

          const colourIndex = this.colourIndex(flask.name);

          this.labelList.push({
            deviceFlaskProperty,
            colour: this.coloursList[index][colourIndex],
          });
        }
      });

      if (experimentData.devices.length === index + 1) {
        // Apply initial filters
        this.filteredDataToRender = this.filterData();

        if (isUpdate) {
          this.updateGraph();
        } else {
          this.drawGraph();
        }
      }
    });
  };

  colourIndex(property: string): number {
    const lastChar = property[property.length - 1];
    switch (lastChar) {
      case 'A':
        return 0;
      case 'B':
        return 1;
      case 'C':
        return 2;
      case 'D':
        return 3;
      default:
        return 0;
    }
  }

  /**
   * Efficiently calculate min/max bounds
   */
  private getDataBounds(data: IRenderData[]): {
    minX: number;
    maxX: number;
    minY: number;
    maxY: number;
  } {
    if (data.length === 0) {
      return { minX: 0, maxX: 1, minY: 0, maxY: 1 };
    }

    let minX = Infinity;
    let maxX = -Infinity;
    let minY = Infinity;
    let maxY = -Infinity;

    for (const point of data) {
      if (point.Time < minX) minX = point.Time;
      if (point.Time > maxX) maxX = point.Time;
      if (point.Value < minY) minY = point.Value;
      if (point.Value > maxY) maxY = point.Value;
    }

    return { minX, maxX, minY, maxY };
  }

  private getXAxisConfig(): Partial<Layout['xaxis']> {
    const bounds = this.getDataBounds(this.filteredDataToRender);
    const minX =
      Number.parseFloat(this.advancedSettingsForm().controls[this.xMinRangeField().key]?.value) ||
      bounds.minX;
    const maxX =
      Number.parseFloat(this.advancedSettingsForm().controls[this.xMaxRangeField().key]?.value) ||
      bounds.maxX;
    const xVal = (maxX - minX) * 0.05;

    return {
      title: {
        text: 'Time [h]',
        font: {
          weight: 600,
          family: 'figtree',
          size: 18,
        },
      },
      range: [minX - xVal, maxX + xVal],
      fixedrange: this.checked(),
      showgrid: true,
      zeroline: false,
      nticks: 10,
      tickmode: 'auto',
    };
  }

  private getYAxisConfig(): Partial<Layout['yaxis']> {
    const bounds = this.getDataBounds(this.filteredDataToRender);
    const minY =
      Number.parseFloat(this.advancedSettingsForm().controls[this.yMinRangeField().key]?.value) ||
      bounds.minY;
    const maxY =
      Number.parseFloat(this.advancedSettingsForm().controls[this.yMaxRangeField().key]?.value) ||
      bounds.maxY;
    const yVal = (maxY - minY) * 0.05;

    return {
      title: {
        text: this.yAxisLabel(),
        font: {
          weight: 600,
          family: 'figtree',
          size: 18,
        },
      },
      type: 'linear',
      range: [minY - yVal, maxY + yVal],
      fixedrange: this.checked(),
      showgrid: true,
      zeroline: false,
      nticks: 10,
      tickmode: 'auto',
    };
  }

  /**
   * Get Plotly symbol based on channel selection index
   * 0 = circle, 1 = square, 2 = triangle, 3 = diamond
   */
  private getSymbol(selectionIndex: number): string {
    if (selectionIndex >= 0 && selectionIndex < this.symbolTypes.length) {
      return this.symbolTypes[selectionIndex];
    }
    return this.symbolTypes[0]; // Default to circle
  }

  /**
   * Get marker size adjusted for symbol type
   * Triangles need to be larger to appear visually similar to other shapes
   */
  private getMarkerSize(baseSize: number, selectionIndex: number): number {
    // Scale triangle (index 2) up by ~1.3 to match other shapes' visual area
    return selectionIndex === 2 ? baseSize * 1.3 : baseSize;
  }

  /**
   * Get channel symbol for template use (by index)
   */
  getChannelSymbol(index: number): string {
    return this.getSymbol(index);
  }

  /**
   * Toggle trace visibility by device name
   */
  toggleTrace(deviceName: string): void {
    const legendItems = this.legendData();
    const deviceIndex = legendItems.findIndex((d) => d.deviceName === deviceName);

    if (deviceIndex === -1) return;

    // Toggle all flasks for this device
    const device = legendItems[deviceIndex];
    const allVisible = device.flasks.every((f) => f.visible);

    this.legendData.update((items) => {
      const updatedItems = [...items];
      updatedItems[deviceIndex] = {
        ...updatedItems[deviceIndex],
        flasks: updatedItems[deviceIndex].flasks.map((flask) => ({
          ...flask,
          visible: !allVisible,
        })),
      };
      return updatedItems;
    });

    // Get all trace indices for this device
    const traceIndices = device.flasks.map((f) => f.traceIndex);

    this.safePlotlyOperation(
      () => Plotly.restyle(
        this.plotlyDiv().nativeElement,
        {
          visible: !allVisible ? true : 'legendonly',
        },
        traceIndices,
      ),
      'Failed to toggle device traces. Please try again.'
    );
  }

  private readonly drawGraph = (): void => {
    const { minY, maxY, minX, maxX } = this.getDataBounds(this.filteredDataToRender);

    const roundedMinY = Number.parseFloat(minY.toFixed(4)) || 0;
    let roundedMaxY = Number.parseFloat(maxY.toFixed(4)) || 1;
    const roundedMinX = Number.parseFloat(minX.toFixed(4)) || 0;
    let roundedMaxX = Number.parseFloat(maxX.toFixed(4)) || 1;

    if (roundedMinX === roundedMaxX) {
      roundedMaxX = roundedMinX + 1;
    }
    if (roundedMinY === roundedMaxY) {
      roundedMaxY = roundedMinY + 1;
    }

    this.advancedSettingsForm().patchValue({
      [this.xMinRangeField().key]: roundedMinX.toString(),
      [this.xMaxRangeField().key]: roundedMaxX.toString(),
      [this.yMinRangeField().key]: roundedMinY.toString(),
      [this.yMaxRangeField().key]: roundedMaxY.toString(),
    });

    // Calculate dimensions
    const containerWidth = this.plotlyDiv()?.nativeElement?.offsetWidth || 800;
    this.width = containerWidth - (this.margin.left + this.margin.right);
    const currentHeight = this.width / 1.8;
    this.height = Math.min(currentHeight, this.maxHeight);
    this.updateSizeFormValues();

    // Set default form values
    this.advancedSettings = new AdvancedSettings(
      roundedMinX,
      roundedMaxX,
      roundedMinY,
      roundedMaxY,
      Number.parseInt(this.height.toFixed(2)),
      Number.parseInt(this.width.toFixed(2)),
      this.ratioField().value,
    );

    // Group data by DeviceFlaskProperty and channel selection
    const groupedData = new Map<string, IRenderData[]>();
    this.filteredDataToRender.forEach((point) => {
      const key = `${point.DeviceFlaskProperty}_ch${point.Selection}`;
      if (!groupedData.has(key)) {
        groupedData.set(key, []);
      }
      groupedData.get(key)!.push(point);
    });

    // Determine if we should use WebGL
    const totalPoints = this.filteredDataToRender.length;
    const useWebGL = totalPoints > 5000;
    const traceType = useWebGL ? 'scattergl' : 'scatter';

    // Optimise marker size for very large datasets
    const markerSize = totalPoints > 50000 ? 4 : totalPoints > 10000 ? 6 : 8;
    this.baseMarkerSize = markerSize;

    // Create traces and build custom legend data
    const seenDevices = new Set<string>();
    const traces: Data[] = [];
    const legendItems: ILegendDevice[] = [];
    const deviceMap = new Map<string, ILegendDevice>();
    const channels = this.fluorescenceChannels();

    Array.from(groupedData.entries()).forEach(([key, points]) => {
      const baseKey = key.replace(/_ch\d+$/, '');
      const label = this.labelList.find((l) => l.deviceFlaskProperty === baseKey);
      const color = label?.colour || '#000000';
      const deviceId = points[0]?.DeviceName || 'Unknown Device';
      const device = this.devices()?.find((d) => d.id.toString() === deviceId);
      const deviceName = device?.name || deviceId;
      const flaskName = points[0]?.FlaskName || key;
      const selectionIndex = points[0]?.Selection ?? 0;
      const channelName = channels?.[selectionIndex]?.channelName || `Channel ${selectionIndex}`;

      // Track device for legend
      if (!seenDevices.has(deviceId)) {
        seenDevices.add(deviceId);
        const legendDevice: ILegendDevice = {
          deviceId,
          deviceName,
          flasks: [],
        };
        deviceMap.set(deviceId, legendDevice);
        legendItems.push(legendDevice);
      }

      const traceIndex = traces.length;

      // Sort points by time for proper line display
      const sortedPoints = [...points].sort((a, b) => a.Time - b.Time);

      // Add flask trace with appropriate symbol
      traces.push({
        x: sortedPoints.map((p) => p.Time),
        y: sortedPoints.map((p) => p.Value),
        mode: this.graphMode(),
        type: traceType,
        name: `${flaskName} (${channelName})`,
        marker: {
          color: color,
          size: this.getMarkerSize(markerSize, selectionIndex),
          symbol: this.getSymbol(selectionIndex),
        },
        line: {
          color: color,
          width: 1,
        },
        hovertemplate:
          '<b>Device:</b> %{customdata[0]}<br>' +
          '<b>Flask:</b> %{customdata[1]}<br>' +
          '<b>Channel:</b> %{customdata[2]}<br>' +
          '<b>Time:</b> %{x}<br>' +
          '<b>Value:</b> %{y}' +
          '<extra></extra>',
        customdata: sortedPoints.map((p) => [p.DeviceName, p.FlaskName, channelName]),
      } as Data);

      // Add to legend data (only add unique flask names per device)
      const flaskLetter = flaskName.slice(-1);
      const existingFlask = deviceMap.get(deviceId)?.flasks.find(f => f.name === flaskLetter);
      if (!existingFlask) {
        deviceMap.get(deviceId)?.flasks.push({
          name: flaskLetter,
          colour: color,
          visible: true,
          traceIndex,
        });
      }
    });

    const layout: Partial<Layout> = {
      width: this.width + this.margin.left + this.margin.right,
      height: this.height + this.margin.top + this.margin.bottom,
      xaxis: this.getXAxisConfig(),
      yaxis: this.getYAxisConfig(),
      showlegend: false,
      margin: {
        l: this.margin.left,
        r: this.margin.right,
        t: this.margin.top,
        b: this.margin.bottom,
      },
      hoverlabel: {
        bgcolor: '#B4DFFF',
        bordercolor: '#0C7BB4',
        font: {
          family: 'figtree',
          size: 13,
        },
      },
      dragmode: 'zoom',
      hovermode: totalPoints > 50000 ? false : 'closest',
    };

    const config: Partial<Config> = {
      scrollZoom: true,
      displayModeBar: false,
      displaylogo: false,
      responsive: true,
      doubleClick: 'reset',
      staticPlot: false,
    };

    // Update legend data signal
    this.legendData.set(legendItems);

    try {
      Plotly.newPlot(this.plotlyDiv().nativeElement, traces, layout, config);
      this.graphReady = true;
      this.errorMessage = null;

      // Store initial axis ranges for zoom scale calculation
      const initialLayout = this.plotlyDiv().nativeElement.layout;
      this.initialXRange = initialLayout.xaxis?.range ? [...initialLayout.xaxis.range] as [number, number] : null;
      this.initialYRange = initialLayout.yaxis?.range ? [...initialLayout.yaxis.range] as [number, number] : null;

      // Handle layout changes (pan, zoom, etc.)
      this.plotlyDiv().nativeElement.on('plotly_relayout', () => {
        this.clearTooltips();
        this.updateMarkerSizeOnZoom();
      });
    } catch (error) {
      this.graphReady = false;
      this.errorMessage = 'Failed to render graph. Please try refreshing the page.';
    }
  };

  private updateGraph(): void {
    if (!this.plotlyDiv()?.nativeElement) return;

    // Group data by DeviceFlaskProperty and channel selection
    const groupedData = new Map<string, IRenderData[]>();
    this.filteredDataToRender.forEach((point) => {
      const key = `${point.DeviceFlaskProperty}_ch${point.Selection}`;
      if (!groupedData.has(key)) {
        groupedData.set(key, []);
      }
      groupedData.get(key)!.push(point);
    });

    // Determine if we should use WebGL
    const totalPoints = this.filteredDataToRender.length;
    const useWebGL = totalPoints > 5000;
    const traceType = useWebGL ? 'scattergl' : 'scatter';

    // Optimise marker size
    const markerSize = totalPoints > 50000 ? 4 : totalPoints > 10000 ? 6 : 8;
    this.baseMarkerSize = markerSize;

    // Create traces and rebuild custom legend data
    const seenDevices = new Set<string>();
    const traces: Data[] = [];
    const legendItems: ILegendDevice[] = [];
    const deviceMap = new Map<string, ILegendDevice>();
    const channels = this.fluorescenceChannels();

    Array.from(groupedData.entries()).forEach(([key, points]) => {
      const baseKey = key.replace(/_ch\d+$/, '');
      const label = this.labelList.find((l) => l.deviceFlaskProperty === baseKey);
      const color = label?.colour || '#000000';
      const deviceId = points[0]?.DeviceName || 'Unknown Device';
      const device = this.devices()?.find((d) => d.id.toString() === deviceId);
      const deviceName = device?.name || deviceId;
      const flaskName = points[0]?.FlaskName || key;
      const selectionIndex = points[0]?.Selection ?? 0;
      const channelName = channels?.[selectionIndex]?.channelName || `Channel ${selectionIndex}`;

      // Track device for legend
      if (!seenDevices.has(deviceId)) {
        seenDevices.add(deviceId);
        const legendDevice: ILegendDevice = {
          deviceId,
          deviceName,
          flasks: [],
        };
        deviceMap.set(deviceId, legendDevice);
        legendItems.push(legendDevice);
      }

      const traceIndex = traces.length;

      // Sort points by time for proper line display
      const sortedPoints = [...points].sort((a, b) => a.Time - b.Time);

      // Add flask trace
      traces.push({
        x: sortedPoints.map((p) => p.Time),
        y: sortedPoints.map((p) => p.Value),
        mode: this.graphMode(),
        type: traceType,
        name: `${flaskName} (${channelName})`,
        marker: {
          color: color,
          size: this.getMarkerSize(markerSize, selectionIndex),
          symbol: this.getSymbol(selectionIndex),
        },
        line: {
          color: color,
          width: 1,
        },
        hoverlabel: {
          bgcolor: '#B4DFFF',
          bordercolor: '#0C7BB4',
          font: {
            family: 'figtree',
            size: 13,
          },
        },
        hovertemplate:
          '<b>Device:</b> %{customdata[0]}<br>' +
          '<b>Flask:</b> %{customdata[1]}<br>' +
          '<b>Channel:</b> %{customdata[2]}<br>' +
          '<b>Time:</b> %{x}<br>' +
          '<b>Value:</b> %{y}' +
          '<extra></extra>',
        customdata: sortedPoints.map((p) => [p.DeviceName, p.FlaskName, channelName]),
      } as Data);

      // Add to legend data
      const flaskLetter = flaskName.slice(-1);
      const existingFlask = deviceMap.get(deviceId)?.flasks.find(f => f.name === flaskLetter);
      if (!existingFlask) {
        deviceMap.get(deviceId)?.flasks.push({
          name: flaskLetter,
          colour: color,
          visible: true,
          traceIndex,
        });
      }
    });

    const layout: Partial<Layout> = {
      xaxis: this.getXAxisConfig(),
      yaxis: this.getYAxisConfig(),
      showlegend: false,
      margin: {
        l: this.margin.left,
        r: this.margin.right,
        t: this.margin.top,
        b: this.margin.bottom,
      },
    };

    // Update legend data signal
    this.legendData.set(legendItems);

    try {
      Plotly.react(this.plotlyDiv().nativeElement, traces, layout);
      this.errorMessage = null;
    } catch (error) {
      this.errorMessage = 'Failed to update graph with new data. Please try refreshing.';
    }
  }

  toggleLegendItem(deviceIndex: number, flaskIndex: number): void {
    this.legendData.update((items) => {
      const updatedItems = [...items];
      updatedItems[deviceIndex] = {
        ...updatedItems[deviceIndex],
        flasks: updatedItems[deviceIndex].flasks.map((flask, idx) =>
          idx === flaskIndex ? { ...flask, visible: !flask.visible } : flask,
        ),
      };
      return updatedItems;
    });

    const flask = this.legendData()[deviceIndex].flasks[flaskIndex];

    // Find all traces that match this flask letter
    const flaskLetter = flask.name;
    const tracesToToggle: number[] = [];

    this.plotlyDiv().nativeElement.data.forEach((trace: any, index: number) => {
      if (trace.name?.includes(flaskLetter)) {
        tracesToToggle.push(index);
      }
    });

    this.safePlotlyOperation(
      () => Plotly.restyle(
        this.plotlyDiv().nativeElement,
        {
          visible: flask.visible ? true : 'legendonly',
        },
        tracesToToggle,
      ),
      'Failed to toggle legend item. Please try again.'
    );
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.graphReady = false;
    if (this.plotlyDiv()?.nativeElement) {
      try {
        Plotly.purge(this.plotlyDiv().nativeElement);
      } catch (error) {
        // Silently fail on cleanup
      }
    }
  }
}

export interface IRenderData {
  DeviceFlaskProperty: string;
  Time: number;
  Value: number;
  DeviceName: string;
  FlaskName: string;
  Selection?: number;
  MemorySlot?: number;
}

export interface IPropertyColour {
  deviceFlaskProperty: string;
  colour: string;
}

export interface ILegendFlask {
  name: string;
  colour: string;
  visible: boolean;
  traceIndex: number;
}

export interface ILegendDevice {
  deviceId: string;
  deviceName: string;
  flasks: ILegendFlask[];
}
